<template>
  <div class="card p-4 shadow-sm">
    <!-- Botón de regreso -->
    <button
      class="btn btn-outline-secondary btn-sm mb-3"
      @click="$router.push('/inicio')"
      type="button"
    >
      <i class="bi bi-arrow-left me-1"></i> Regresar
    </button>

    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0">Reporte de Camas por Habitación</h4>
      <button
        type="button"
        class="btn btn-danger"
        @click="descargarPDF"
        :disabled="!reporte || cargando"
        title="Descargar reporte en PDF"
      >
        <i class="bi bi-file-pdf me-1"></i> Descargar PDF
      </button>
    </div>

    <!-- Loading -->
    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
      <span class="ms-2 text-muted">Cargando...</span>
    </div>

    <!-- Resultado -->
    <div v-if="reporte && !cargando">
      <div class="text-muted small mb-3">
        <strong>Fecha:</strong> {{ formatearFecha(reporte.fecha) }}
      </div>

      <!-- Tarjetas de Totales -->
      <div class="row mb-4">
        <div class="col-md-3">
          <div class="card bg-primary text-white text-center">
            <div class="card-body">
              <h5>{{ reporte.totales.TotalCamas }}</h5>
              <small>Total Camas</small>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card bg-danger text-white text-center">
            <div class="card-body">
              <h5>{{ reporte.totales.TotalCamasOcupadas }}</h5>
              <small>Camas Ocupadas</small>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card bg-success text-white text-center">
            <div class="card-body">
              <h5>{{ reporte.totales.TotalCamasDisponibles }}</h5>
              <small>Camas Disponibles</small>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card bg-warning text-dark text-center">
            <div class="card-body">
              <h5>{{ reporte.totales.HabitacionesConSobrante }}</h5>
              <small>Habitaciones con Sobrante</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Porcentaje de Ocupación General -->
      <div class="alert alert-info text-center mb-4">
        <strong>Ocupación General:</strong> {{ reporte.totales.PorcentajeOcupacionGeneral }}%
      </div>

      <!-- Filtros -->
      <div class="mb-3">
        <div class="btn-group" role="group">
          <button
            type="button"
            class="btn btn-sm"
            :class="filtro === 'todas' ? 'btn-primary' : 'btn-outline-primary'"
            @click="filtro = 'todas'"
          >
            Todas
          </button>
          <button
            type="button"
            class="btn btn-sm"
            :class="filtro === 'ocupadas' ? 'btn-primary' : 'btn-outline-primary'"
            @click="filtro = 'ocupadas'"
          >
            Solo Ocupadas
          </button>
          <button
            type="button"
            class="btn btn-sm"
            :class="filtro === 'sobrante' ? 'btn-primary' : 'btn-outline-primary'"
            @click="filtro = 'sobrante'"
          >
            Con Camas Sobrantes
          </button>
          <button
            type="button"
            class="btn btn-sm"
            :class="filtro === 'disponibles' ? 'btn-primary' : 'btn-outline-primary'"
            @click="filtro = 'disponibles'"
          >
            Disponibles
          </button>
        </div>
      </div>

      <!-- Tabla de Habitaciones -->
      <div class="table-responsive">
        <table class="table table-bordered table-hover text-center">
          <thead class="table-light">
            <tr>
              <th>Habitación</th>
              <th>Tipo</th>
              <th>Total Camas</th>
              <th>Personas</th>
              <th>Niños</th>
              <th>Camas Ocupadas</th>
              <th>Camas Disponibles</th>
              <th>Ocupación %</th>
              <th>Huésped</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="hab in habitacionesFiltradas"
              :key="hab.NumeroHabitacion"
              :class="getRowClass(hab)"
            >
              <td><strong>{{ hab.NumeroHabitacion }}</strong></td>
              <td>{{ hab.TipoHabitacion }}</td>
              <td>{{ hab.CantidadCamas }}</td>
              <td>{{ hab.PersonasAlojadas }}</td>
              <td>{{ hab.Ninios }}</td>
              <td>
                <span class="badge bg-danger">{{ hab.CamasOcupadas }}</span>
              </td>
              <td>
                <span
                  class="badge"
                  :class="hab.CamasDisponibles > 0 ? 'bg-success' : 'bg-secondary'"
                >
                  {{ hab.CamasDisponibles }}
                </span>
              </td>
              <td>
                <div class="progress" style="height: 20px;">
                  <div
                    class="progress-bar"
                    :class="getProgressBarClass(hab.PorcentajeOcupacion)"
                    :style="{ width: hab.PorcentajeOcupacion + '%' }"
                  >
                    {{ hab.PorcentajeOcupacion }}%
                  </div>
                </div>
              </td>
              <td>
                <small>{{ hab.NombreHuesped || '—' }}</small>
              </td>
              <td>
                <span
                  class="badge"
                  :class="hab.Ocupada ? 'bg-danger' : 'bg-success'"
                >
                  {{ hab.Ocupada ? 'Ocupada' : 'Libre' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="habitacionesFiltradas.length === 0" class="text-muted text-center py-3">
          No hay habitaciones para mostrar con el filtro seleccionado.
        </div>
      </div>

      <!-- Resumen de Habitaciones con Sobrante -->
      <div v-if="habitacionesConSobrante.length > 0" class="mt-4">
        <div class="alert alert-warning">
          <h6 class="mb-3"><i class="bx bx-info-circle"></i> Habitaciones con Camas Sobrantes:</h6>
          <ul class="mb-0">
            <li v-for="hab in habitacionesConSobrante" :key="hab.NumeroHabitacion">
              <strong>Habitación {{ hab.NumeroHabitacion }}</strong> ({{ hab.TipoHabitacion }}):
              {{ hab.CamasDisponibles }} cama(s) disponible(s) de {{ hab.CantidadCamas }} total(es)
              <span v-if="hab.NombreHuesped" class="text-muted"> - {{ hab.NombreHuesped }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <div v-if="mostrarToast" class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1055;">
      <div class="toast show align-items-center text-white bg-danger">
        <div class="d-flex">
          <div class="toast-body">
            <i class="bx bx-error me-2"></i> {{ alerta.mensaje }}
          </div>
          <button type="button" class="btn-close btn-close-white me-2 m-auto" @click="cerrarToast"></button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_URL } from "@/config/config.js";

export default {
  name: "ReporteCamas",
  data() {
    return {
      reporte: null,
      cargando: false,
      mostrarToast: false,
      alerta: { mensaje: "" },
      filtro: 'todas' // todas, ocupadas, sobrante, disponibles
    };
  },
  computed: {
    habitacionesFiltradas() {
      if (!this.reporte || !this.reporte.habitaciones) return [];

      let habitaciones = this.reporte.habitaciones;

      switch (this.filtro) {
        case 'ocupadas':
          return habitaciones.filter(h => h.Ocupada);
        case 'sobrante':
          return habitaciones.filter(h => h.Ocupada && h.CamasDisponibles > 0);
        case 'disponibles':
          return habitaciones.filter(h => !h.Ocupada);
        default:
          return habitaciones;
      }
    },
    habitacionesConSobrante() {
      if (!this.reporte || !this.reporte.habitaciones) return [];
      return this.reporte.habitaciones.filter(h => h.Ocupada && h.CamasDisponibles > 0);
    }
  },
  methods: {
    async obtenerReporte() {
      this.cargando = true;
      this.reporte = null;
      try {
        const res = await axios.get(`${API_URL}/reporte-camas-habitacion`);
        this.reporte = res.data;
      } catch (error) {
        console.error("Error obteniendo reporte:", error);
        this.mostrarAlerta("Error al obtener el reporte");
      } finally {
        this.cargando = false;
      }
    },
    formatearFecha(fecha) {
      if (!fecha) return "";
      const [year, month, day] = fecha.split("-");
      return `${day}/${month}/${year}`;
    },
    getRowClass(hab) {
      if (!hab.Ocupada) return 'table-light';
      if (hab.CamasDisponibles > 0) return 'table-warning';
      return '';
    },
    getProgressBarClass(porcentaje) {
      if (porcentaje === 0) return 'bg-secondary';
      if (porcentaje < 50) return 'bg-success';
      if (porcentaje < 80) return 'bg-warning';
      return 'bg-danger';
    },
    mostrarAlerta(mensaje) {
      this.alerta = { mensaje };
      this.mostrarToast = true;
      setTimeout(this.cerrarToast, 3000);
    },
    cerrarToast() {
      this.mostrarToast = false;
    },
    async descargarPDF() {
      if (!this.reporte) {
        this.mostrarAlerta('No hay datos para generar el PDF');
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/reporte-camas-habitacion-pdf`, {
          params: { fecha: this.reporte.fecha },
          responseType: 'blob'
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `Reporte_Camas_${this.reporte.fecha}.pdf`;
        link.click();
        window.URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error('Error descargando PDF:', error);
        this.mostrarAlerta('Error al generar el PDF');
      }
    }
  },
  mounted() {
    this.obtenerReporte();
  }
};
</script>

<style scoped>
.card {
  border-radius: 8px;
}

.table th {
  font-size: 0.875rem;
  font-weight: 600;
}

.table td {
  vertical-align: middle;
}

.progress {
  border-radius: 10px;
}

.progress-bar {
  font-size: 0.75rem;
  font-weight: bold;
}

.btn-group .btn {
  font-size: 0.875rem;
}

.alert ul {
  padding-left: 1.5rem;
}

.alert ul li {
  margin-bottom: 0.5rem;
}
</style>
